import capitalize from 'lodash/capitalize';
import numeral from 'numeral';

interface Price {
  frequency: string;
  name: string;
  price: number;
  stripe_price: string;
  subscription_type: string;
}

interface TrialPrices {
  forProUsers: Price[];
  forUsers: Price[];
}

const exitSurveyOptions = [
  { commentRequired: false, key: 1, value: 'No longer brokering/changed role' },
  { commentRequired: false, key: 2, value: 'Using a different tool/application' },
  { commentRequired: false, key: 3, value: 'Business slow/new to brokering' },
  { commentRequired: false, key: 4, value: 'Cost too high' },
  { commentRequired: true, key: 7, value: 'Missing important features for my business' },
  { commentRequired: true, key: 8, value: 'Other' },
];

const platinumOnlyExitSurveyOptions = [
  { commentRequired: false, key: 5, value: 'Connected to Brokerage or Finmo' },
  { commentRequired: false, key: 6, value: 'Get enough value from Lender Spotlight Basic' },
];

export const addonExitSurveyOptions = exitSurveyOptions;

export const platinumExitSurveyOptions = exitSurveyOptions
  .concat(platinumOnlyExitSurveyOptions)
  .sort((a, b) => a.key - b.key);

/**
 * Calculates the discount price of a subscription
 */
export const discount = (subscription: { price?: number }, discount: { percent_off?: number }) =>
  (subscription.price as number) - ((subscription.price as number) * (discount.percent_off as number)) / 100;

/**
 * Converts a price object to a currency/frequency string
 */
export const price = (price: { frequency?: string; price?: number }) =>
  `${numeral((price.price as number) / 100).format('$0,0')} / ${capitalize(price.frequency)}`;

/**
 * Converts cents to a concise currency string for trial offer summaries.
 */
export const trialCurrency = (cents: number) => numeral(cents / 100).format('$0,0[.]00');

/**
 * Converts a price object to a trial offer subscription item.
 */
export const trialSubscription = (price: Price) => ({
  name: price.subscription_type === 'platinum' ? 'Platinum' : price.name,
  price: `${trialCurrency(price.price)} / ${capitalize(price.frequency)}`,
});

/**
 * Converts price objects to a TrialOfferSummary payload.
 */
export const trialOffer = (prices: Price[]) => ({
  originalPrice: trialCurrency(prices.reduce((total, price) => total + price.price, 0)),
  subscriptions: prices.map(trialSubscription),
});

/**
 * Selects the prices included in a free trial.
 */
export const trialPrices = (prices: TrialPrices, addonOnly = false) =>
  [
    ...(addonOnly ? [] : [prices.forUsers.find((price) => price.stripe_price === 'platinum-solo-monthly')]),
    ...prices.forProUsers,
  ].filter((price): price is Price => Boolean(price));
